/**
 * Cursor pagination loop shared by the My Cases, My Tasks and case Tasks hooks. Repeatedly calls
 * `fetchPage` until any of: server signals no more results (empty `next_cursor`),
 * server returns a short page (`records.length < pageSize`), or the `maxPages`
 * cap is reached.
 *
 * Returns `reachedCap = true` only when the loop exhausts all `maxPages`
 * iterations with a non-empty `next_cursor` still pending — i.e. there is
 * genuinely more data beyond the cap. A loop that drains naturally at the cap
 * (last page returns an empty cursor or short page) returns `reachedCap =
 * false`. This is what callers should use to decide whether to surface a
 * "showing first N" warning; a row-count comparison alone false-positives when
 * the dataset happens to fill exactly `maxPages * pageSize`.
 *
 * Accumulated records are de-duplicated by id (first occurrence wins).
 * Correct cursor pagination never returns the same row on two pages, but a
 * server-side sort/seek-direction mismatch can make pages overlap; because this
 * loop concatenates pages, that would otherwise render duplicate rows and
 * inflate the All/Pending counts. Dedup here is the last line of defense so the
 * UI stays correct regardless of backend paging bugs.
 */
export const paginateCursorList = async <T extends { id?: string }>(
  fetchPage: (cursor: string | undefined) => Promise<{ records?: T[]; next_cursor?: string }>,
  maxPages: number,
  pageSize: number,
): Promise<{ records: T[]; reachedCap: boolean }> => {
  const records: T[] = [];
  const seenIds = new Set<string>();
  let cursor: string | undefined;
  let reachedCap = false;
  for (let page = 0; page < maxPages; page++) {
    const response = await fetchPage(cursor);
    const pageRecords = response.records ?? [];
    for (const rec of pageRecords) {
      // Skip a row we've already accumulated from an earlier (overlapping) page.
      // Rows without an id can't be de-duplicated, so keep them as-is.
      if (rec.id) {
        if (seenIds.has(rec.id)) continue;
        seenIds.add(rec.id);
      }
      records.push(rec);
    }
    cursor = response.next_cursor || undefined;
    // Loop control keys off the RAW page length (what the server returned), not
    // the post-dedup count — a full page still means "there may be more".
    if (!cursor || pageRecords.length < pageSize) break;
    // The final iteration completed with a full page AND a non-empty cursor —
    // there's more data we didn't fetch.
    if (page === maxPages - 1) reachedCap = true;
  }
  return { records, reachedCap };
};
